import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  getQuestionnaireTemplateConfig,
  getQuestionnaireTemplateConfigByPath,
} from '../constants/questionnaire-template-registry';

export interface QuestionnaireNavigationContext {
  routeSection?: string;
  sectionPath?: string;
  returnToSection?: boolean;
}

export type QuestionnaireScreenMode =
  | 'profile-readonly'
  | 'visit-add'
  | 'visit-edit';

export type QuestionnaireScreenSource =
  | 'profile'
  | 'episodeOfCare'
  | 'inPatientView';

export interface QuestionnaireScreenState {
  mode: QuestionnaireScreenMode;
  source: QuestionnaireScreenSource;
  routeSection: string;
  questionnaireTemplateId: number;
  questionnaireDataId?: string;
}

@Injectable({ providedIn: 'root' })
export class QuestionnaireNavigationService {
  private readonly contextSub =
    new BehaviorSubject<QuestionnaireNavigationContext>({});
  private readonly screenSub =
    new BehaviorSubject<QuestionnaireScreenState | null>(null);

  readonly context$ = this.contextSub.asObservable();
  readonly screen$ = this.screenSub.asObservable();

  getContext(): QuestionnaireNavigationContext {
    return this.contextSub.value;
  }

  setContext(context: QuestionnaireNavigationContext): void {
    this.contextSub.next({
      routeSection: context.routeSection,
      sectionPath: context.sectionPath,
      returnToSection: context.returnToSection ?? false,
    });
  }

  getScreen(): QuestionnaireScreenState | null {
    return this.screenSub.value;
  }

  openScreen(state: QuestionnaireScreenState): void {
    this.setTemplateContext(state.questionnaireTemplateId);
    this.screenSub.next({ ...state });
  }

  closeScreen(): void {
    this.screenSub.next(null);
  }

  setTemplateContext(templateId?: number): void {
    const template = getQuestionnaireTemplateConfig(templateId);

    if (!template) {
      return;
    }

    this.setContext({
      routeSection: template.routeSection,
      sectionPath: template.sectionPath,
    });
  }

  setSectionContext(sectionPath?: string): void {
    const template = getQuestionnaireTemplateConfigByPath(sectionPath);

    if (template) {
      this.setTemplateContext(template.id);
      return;
    }

    this.setContext({
      routeSection: sectionPath,
      sectionPath,
    });
  }

  requestReturnToSection(templateId?: number): void {
    const template = getQuestionnaireTemplateConfig(templateId);

    if (!template) {
      return;
    }

    this.setContext({
      routeSection: template.routeSection,
      sectionPath: template.sectionPath,
      returnToSection: true,
    });
  }

  consumeReturnToSection(): string | undefined {
    const context = this.getContext();

    if (!context.returnToSection || !context.sectionPath) {
      return undefined;
    }

    this.setContext({
      routeSection: context.routeSection,
      sectionPath: context.sectionPath,
      returnToSection: false,
    });

    return context.sectionPath;
  }
}
